











































































































































import { formatYearsMonthDay, simpleDate, simpleDoubleDigitDate } from "@/lib/utility/date-helper";
import { $t } from "@/lib/utility/t";
import DarkModeHighlightMixin from "@/mixins/DarkModeHighlightMixin.vue";
import { ISharedContent } from "@/models/shared-content.entity";
import { BackendResourceEnum, ResourceEnum } from "@/store/enum/authResourceEnum";
import { debounce } from "debounce";
import { Component, Prop } from "vue-property-decorator";
import LatestEntriesCardEmpty from "../cards/LatestEntriesCardEmpty.vue";
import Card from "../utility/Card.vue";
import ConfirmActionDialog from "../utility/ConfirmActionDialog.vue";
import RefsSelect from "../utility/RefsSelect.vue";
import MHeader, { IAction } from "../utility/mmmint/MHeader.vue";
import Tooltip from "../utility/tooltip.vue";
import { IRefSuggestion } from "@/store/modules/fleet-aggregation.store";
import { PartnerUserModule } from "@/store/modules/partner-user.store";
import { IBreadcrumb } from "@/lib/interfaces/utility/breadcrumb-interface";
import { IPageFilterElement } from "@/models/page-filter-element.entity";
import { IReference, Reference } from "@/models/reference.entity";
import { mixins } from "vue-class-component";
import PermissionMixin from "@/mixins/PermissionMixin.vue";
import { ActionEnum } from "@/store/enum/authActionEnum";
import { IReport } from "@/models/report.entity";
import { fetchReference } from "@/lib/utility/fetch-reference.helper";
import MNoContent from "../utility/mmmint/MNoContent.vue";

@Component({
  components: { LatestEntriesCardEmpty, Card, ConfirmActionDialog, MHeader, Tooltip, RefsSelect, MNoContent }
})
class SharedContentDetail extends mixins(DarkModeHighlightMixin, PermissionMixin) {
  readonly BackendResourceEnum = BackendResourceEnum;

  @Prop()
  value!: ISharedContent;

  @Prop({ default: false })
  hideBreadCrumbs!: boolean;

  @Prop({ default: () => [] })
  suggested!: IRefSuggestion[];

  @Prop({ default: () => [] })
  filters!: IPageFilterElement[];

  @Prop({
    default: () => [
      ResourceEnum.REPORT,
      ResourceEnum.DOCUMENT,
      ResourceEnum.SNAPSHOT,
      ResourceEnum.ATTACHMENT_REQUEST,
      ResourceEnum.SIGN,
      ResourceEnum.REPAIR_ESTIMATE,
      ResourceEnum.RENTAL
    ]
  })
  referenceCategories!: ResourceEnum[];

  readonly EDIT_TITLE = "editTitle";
  readonly DELETE = "delete";
  readonly COPY = "copy";
  readonly DEACTIVATE = "deactivate";

  isDeleteDialogActive = false;

  isEditTitle = false;

  isEditDescription = false;

  editTitle = "";

  editDescription = "";

  debounceUpdate = debounce(() => {
    this.value.update();
  }, 500);

  contactSuggestions: IRefSuggestion[] = [];

  get contactRefs(): IReference[] {
    return this.value.contactIds.map(
      contactId =>
        new Reference({
          refId: contactId,
          refType: BackendResourceEnum.PERSON
        })
    );
  }

  get datePickerMinimum() {
    return formatYearsMonthDay(new Date());
  }

  get validByDatePickerValue() {
    if (!this.value.validBy) {
      return "";
    }

    return formatYearsMonthDay(new Date(this.value.validBy));
  }

  set validByDatePickerValue(value: string) {
    this.value.validBy = new Date(value).toISOString();
  }

  get validBy() {
    if (this.value.isExpired) {
      return $t("views.PartnerSharedReport.expired");
    } else if (this.value.validBy) {
      return $t("timeLine.ActivityTimeLineItemShare.validBy", { date: simpleDate(this.value.validBy) });
    } else {
      return $t("timeLine.ActivityTimeLineItemShare.validIndefinitely");
    }
  }

  get isEntityLoading() {
    return this.value.isLoading;
  }

  get breadCrumbs(): IBreadcrumb[] | undefined {
    if (this.hideBreadCrumbs) {
      return undefined;
    }

    const breadCrumbs: IBreadcrumb[] = [];
    if (this.value.partnerId) {
      breadCrumbs.push({
        text: $t("components.value.OperationTable.title"),
        to: { name: "PartnerOperations", params: { partnerId: this.value.partnerId } },
        exact: true
      });
    } else {
      breadCrumbs.push({
        text: $t("components.value.OperationTable.title"),
        to: { name: "ThgOperations" },
        exact: true
      });
    }

    return breadCrumbs;
  }

  get actions(): IAction[] {
    const actions: IAction[] = [];

    if (!this.value.isExpired) {
      actions.push({
        text: $t("timeLine.ActivityTimeLineItemShare.btnText"),
        key: this.COPY
      });
    }

    if (!this.value.isExpired) {
      actions.push({
        text: $t("timeLine.ActivityTimeLineItemShare.btnTextAlt"),
        key: this.DEACTIVATE
      });
    }

    actions.push({
      text: $t("common.verbs.delete"),
      key: this.DELETE
    });

    if (!this.isEditTitle) {
      actions.push({
        text: $t("project.ticket.editTitle"),
        key: this.EDIT_TITLE
      });
    }
    return actions;
  }

  get subtitle() {
    if (this.value.userId && this.getUserNameForId(this.value.userId)) {
      return $t("createdOnBy", {
        date: simpleDoubleDigitDate(this.value.timestamp.created),
        name: this.getUserNameForId(this.value.userId)
      });
    }

    if (this.value.isSystemGenerated) {
      return $t("createdOnBy", {
        date: simpleDoubleDigitDate(this.value.timestamp.created),
        name: $t("timeLine.ActivityTimeLineItemDocument.systemgenerated")
      });
    }

    return $t("createdOn", { date: simpleDoubleDigitDate(this.value.timestamp.created) });
  }

  async mounted() {
    await this.collectContactSuggestionsFromReport();
  }

  async debounceUpdateContacts(contacts: IReference[]) {
    const contactIds = contacts.map(contact => contact.refId);

    await this.value.updatePartial({ contactIds });
  }

  async collectContactSuggestionsFromReport() {
    if (!this.can(ActionEnum.READ, ResourceEnum.REPORT)) {
      return;
    }

    const reportIds = this.value.refs.filter(ref => ref.refType === BackendResourceEnum.REPORT).map(ref => ref.refId);
    if (this.value.reportId) {
      reportIds.push(this.value.reportId);
    }

    const reportIdsUnique = Array.from(new Set(reportIds));

    const promises = [];
    for (const reportId of reportIdsUnique) {
      promises.push(
        fetchReference(this.value.partnerId, new Reference({ refId: reportId, refType: BackendResourceEnum.REPORT }))
      );
    }

    const reports = await Promise.all(promises).catch(e => this.$log.error(e));

    const contactSuggestions: string[] = [];
    for (const report of reports as IReport[]) {
      if (report.personId) {
        contactSuggestions.push(report.personId);
      }
      if (report.personIds?.length) {
        contactSuggestions.push(...report.personIds);
      }
    }

    const uniqueContactSuggestions = Array.from(new Set(contactSuggestions));
    const uniqueContactSuggestionsThatWereTranformedToReferences: Promise<IRefSuggestion | null>[] = uniqueContactSuggestions.map(
      async contactId => {
        const ref = await fetchReference(
          this.value.partnerId,
          new Reference({ refId: contactId, refType: BackendResourceEnum.PERSON })
        );

        if (!ref) {
          return null;
        }

        const refSuggestion: IRefSuggestion = {
          id: contactId,
          refType: BackendResourceEnum.PERSON,
          ref: ref
        };

        return refSuggestion;
      }
    );

    const resolvedContactSuggestions = await Promise.all(uniqueContactSuggestionsThatWereTranformedToReferences);
    this.contactSuggestions.splice(0);
    this.contactSuggestions.push(...(resolvedContactSuggestions.filter(v => !!v) as IRefSuggestion[]));
  }

  async processAction(action: IAction) {
    switch (action.key) {
      case this.DELETE:
        this.startDeleteDialog();
        break;

      case this.COPY:
        this.value.copyLinkToClipboard();
        break;

      case this.DEACTIVATE:
        this.value.validBy = new Date().toISOString();
        await this.value.update();
        break;

      case this.EDIT_TITLE:
        this.startEditTitle();
        break;
    }
  }

  startEditTitle() {
    this.isEditTitle = true;
    this.editTitle = this.value.title;
  }

  cancelUpdateTitle() {
    this.isEditTitle = false;
  }

  updateTitle() {
    this.value.title = this.editTitle;
    this.isEditTitle = false;
    this.value.update();
  }

  startEditDescription() {
    this.isEditDescription = true;
    this.editDescription = this.value.description;
  }

  cancelUpdateDescription() {
    this.isEditDescription = false;
  }

  updateDescription() {
    this.value.description = this.editDescription;
    this.isEditDescription = false;
    this.value.update();
  }

  startDeleteDialog() {
    this.isDeleteDialogActive = true;
  }

  async onDelete() {
    this.isDeleteDialogActive = false;
    await this.value.delete();
    this.$emit("onDelete");
  }

  getUserNameForId(id: string) {
    const user = PartnerUserModule.maps.id.get(id)[0];

    let name = "";
    if (user) {
      name = `${user.firstName} ${user.lastName}`;
    }

    return name;
  }
}

export default SharedContentDetail;
